<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" sm="9">
        <v-card>
          <v-card-title>
            {{ mode === "CREATE" ? "Create" : "Edit" }} Job
          </v-card-title>
          <v-form ref="form">
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <h3>Basic Info</h3>
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="job.jobType"
                    :items="jobTypeList"
                    label="Select Job Type*"
                    :rules="[rules.required]"
                    data-cy="job-form-select-job-type"
                    @change="hidePayrollProvider()"
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-if="job.jobType != 'DOT'"
                    v-model="job.payrollProvider"
                    :items="payrollCompanyList"
                    item-text="name"
                    item-value="id"
                    label="Select Payroll Company*"
                    :rules="[rules.required]"
                    data-cy="job-form-select-payroll-company"
                  />
                </v-col>
                <v-col cols="9">
                  <v-text-field
                    v-model="job.name"
                    label="Job Name*"
                    :rules="[rules.required]"
                    data-cy="job-form-text-name"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="job.number"
                    label="Job Number*"
                    :rules="[rules.required]"
                    data-cy="job-form-text-number"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="job.price"
                    label="Price*"
                    prefix="$"
                    :rules="[rules.required, rules.numeric]"
                    data-cy="job-form-text-price"
                  />
                </v-col>
                <v-col v-if="job.jobType == 'DOT'" cols="12" md="3">
                  <v-text-field
                    v-model="job.poNumber"
                    label="PO Number*"
                    :rules="[rules.required]"
                    data-cy="job-form-text-po-number"
                  />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="job.notes"
                    label="Notes for the freelancers"
                    class="pt-0"
                    auto-grow
                    rows="2"
                    data-cy="job-form-text-notes"
                  />
                </v-col>
                <v-col cols="6">
                  <CompanySelector
                    v-model="job.company"
                    data-cy="job-form-auto-company"
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="job.market"
                    :items="marketList"
                    item-text="name"
                    label="Market"
                    item-value="id"
                    :rules="[rules.required]"
                    data-cy="job-form-select-market"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-menu
                    v-model="menu2"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="job.endsOn"
                        label="End Date*"
                        :rules="[rules.required]"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="job.endsOn"
                      @input="menu2 = false"
                      :min="new Date().toISOString().substr(0, 10)"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <v-col v-if="job.jobType != 'DOT'" cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="job.supervisorTitle"
                    label="Supervisor Title"
                    data-cy="job-form-supervisor-job"
                  />
                </v-col>

                <v-col cols="12">
                  <h3>Users</h3>
                </v-col>
                <v-col cols="6">
                  <UserSelector
                    v-model="job.manager"
                    data-cy="job-form-auto-manager"
                    label="Manager*"
                    :rules="[rules.required]"
                  />
                </v-col>
                <v-col cols="6">
                  <UserSelector
                    data-cy="job-form-auto-artCoordinator"
                    :value="job.artCoordinator"
                    label="Art Coordinator"
                    @input="
                      (user) => {
                        job.artCoordinator = user;
                      }
                    "
                  />
                </v-col>
                <v-col v-for="(user, i) in job.users" :key="i" cols="12">
                  <v-row align="center" wrap>
                    <v-col cols="10">
                      <UserSelector
                        :data-cy="`job-form-auto-user-${i}`"
                        :value="user"
                        @input="(user) => updateUser(user, i)"
                        label="Additional Timecard Recipients"
                      />
                    </v-col>
                    <v-col cols="2">
                      <v-row wrap>
                        <v-col cols="auto" @click="addUser()">
                          <v-icon :data-cy="`kit-rental-${i}-icon-plus`">
                            mdi-plus
                          </v-icon>
                        </v-col>
                        <v-col
                          v-if="job.users && job.users.length > 1"
                          cols="auto"
                          @click="removeUser(i)"
                        >
                          <v-icon :data-cy="`kit-rental-${i}-icon-minus`">
                            mdi-minus
                          </v-icon>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
                <div v-if="job.jobType != 'DOT'">
                  <v-col cols="12">
                    <h3>Configuration</h3>
                  </v-col>
                  <v-col cols="6">
                    <v-select
                      v-model="job.timeIncrement"
                      :items="timeIncrementList"
                      hint="Allows Freelancers to select 10 vs 15 minute increments when filling out timecards"
                      persistent-hint
                      label="Select Time Increment"
                      :rules="[rules.required]"
                      data-cy="job-form-select-time-increments"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-row justify="start" align="center">
                      <v-col cols="12" md="3">
                        <v-switch v-model="job.hasTravel" label="Travel" />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch
                          v-model="job.hasOutOfZone"
                          label="Out of Zone"
                        />
                      </v-col>
                      <v-col v-if="job.payrollProvider == 1" cols="12" md="3">
                        <v-switch
                          v-model="job.hasEmployeeStartForm"
                          label="CAPS Employee Start Form"
                        />
                      </v-col>
                      <v-col
                        v-else-if="job.payrollProvider == 4"
                        cols="12"
                        md="3"
                      >
                        <v-switch
                          v-model="job.hasEmployeeStartForm"
                          label="Revolution Employee Start Form"
                        />
                      </v-col>
                      <v-col
                        v-else-if="job.payrollProvider == 6"
                        cols="12"
                        md="3"
                      >
                        <v-switch
                          v-model="job.hasEmployeeStartForm"
                          label="Extreme Reach Employee Start Form"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch
                          v-model="job.isNdaIncluded"
                          label="Include NDAs"
                        />
                      </v-col>
                      <v-col
                        v-if="job.market == CALIFORNIA_MARKET_ID"
                        cols="12"
                        md="3"
                      >
                        <v-switch
                          v-model="job.isDe4Included"
                          label="Include DE-4 California Form"
                        />
                      </v-col>
                      <v-col
                        v-if="job.market == CALIFORNIA_MARKET_ID"
                        cols="12"
                        md="3"
                      >
                        <v-switch
                          v-model="job.hasCaSickLeaveNotice"
                          label="California Paid Sick Leave Notice"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch
                          v-model="job.storeWithBox"
                          label="Enable Box Storage"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch
                          v-model="job.isNonUnion"
                          label="Non Union Job"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch
                          v-model="job.hasPrefillMileage"
                          label="Prefill Mileage"
                        />
                      </v-col>
                      <v-col v-if="job.hasPrefillMileage" cols="12" md="3">
                        <v-text-field
                          v-model="job.prefillMileage"
                          label="Mileage Prefill Amount"
                          :rules="[rules.required]"
                          type="number"
                          pattern="\d*"
                          data-cy="job-form-prefill-mileage"
                        />
                      </v-col>
                      <v-col
                        v-if="
                          job.payrollProvider ==
                            MEDIA_SERVICES_PAYROLL_PROVIDER_ID ||
                          job.payrollProvider ==
                            REVOLUTION_PAYROLL_PROVIDER_ID ||
                          job.payrollProvider == EXTREME_PAYROLL_PROVIDER_ID
                        "
                        cols="12"
                      >
                        <h3>Shoot Location</h3>
                        <div v-if="job.shoots.length > 0">
                          <v-row>
                            <v-col>
                              <v-text-field
                                v-model="job.shoots[0].city"
                                label="City"
                                :rules="[rules.required]"
                                data-cy="job-form-text-number"
                            /></v-col>
                            <v-col>
                              <v-text-field
                                v-model="job.shoots[0].county"
                                label="County"
                                :rules="[rules.required]"
                                data-cy="job-form-text-number"
                            /></v-col>
                            <v-col>
                              <v-text-field
                                v-model="job.shoots[0].state"
                                label="State"
                                :rules="[rules.required]"
                                data-cy="job-form-text-number"
                              />
                            </v-col>
                          </v-row>
                        </div>
                        <div v-else>
                          Want to add a shoot location?
                          <v-btn
                            @click="
                              () =>
                                job.shoots.push({
                                  city: '',
                                  county: '',
                                  state: '',
                                })
                            "
                          >
                            Add Shoot Location
                          </v-btn>
                        </div>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch
                          v-model="job.showKitRental"
                          label="Include Kit Rental"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch
                          v-model="job.showMileageForm"
                          label="Include Mileage Form"
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch
                          v-model="job.allowBlankTimecardSubmission"
                          label="Allow Blank Timecard"
                        />
                      </v-col>
                      <v-col
                        v-if="job.jobType == 'Music Video' && job.payrollProvider == 3"
                        cols="12"
                        md="3"
                      >
                        <v-switch
                          v-model="job.removeMealPenalty"
                          label="Remove Meal Penalty"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                </div>
              </v-row>
              <v-row justify="end">
                <v-col cols="auto">
                  <v-btn
                    text
                    outlined
                    :to="{ name: 'jobs' }"
                    data-cy="job-form-btn-cancel"
                  >
                    cancel
                  </v-btn>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    color="primary"
                    data-cy="job-form-btn-submit"
                    @click="submit"
                  >
                    {{ mode === "CREATE" ? "Submit" : "Update" }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import rules from "../utils/rules";
import { createJob, getJob, updateJob } from "../services/jobs";
import { CALIFORNIA_MARKET_ID, getMarkets } from "../services/markets";
import UserSelector from "../components/UserSelector.vue";
import CompanySelector from "../components/CompanySelector.vue";
import { DateTime } from "luxon";
import {
  getPayrollProviders,
  MEDIA_SERVICES_PAYROLL_PROVIDER_ID,
  REVOLUTION_PAYROLL_PROVIDER_ID,
  EXTREME_PAYROLL_PROVIDER_ID,
} from "../services/payrollProviders";

export default {
  components: {
    UserSelector,
    CompanySelector,
  },
  props: {
    mode: {
      type: String,
      default: "EDIT",
    },
  },
  data() {
    return {
      MEDIA_SERVICES_PAYROLL_PROVIDER_ID,
      REVOLUTION_PAYROLL_PROVIDER_ID,
      EXTREME_PAYROLL_PROVIDER_ID,
      CALIFORNIA_MARKET_ID,
      payDayFrequency: "",
      payDayFrequencyOther: "",
      job: {
        notes: "",
        manager: {},
        artCoordinator: {},
        users: [
          {
            id: "",
            list: [],
          },
        ],
        endsOn: "",
        jobType: "",
        hasPaid: false,
        isNdaIncluded: false,
        isDe4Included: false,
        hasCaSickLeaveNotice: false,
        removeMealPenalty: false,
        storeWithBox: false,
        payrollProvider: "",
        payDay: "",
        timeIncrement: "15 Minute Increment",
        shoots: [],
        isNonUnion: false,
        supervisorTitle: "Production Supervisor",
        poNumber: "",
        showKitRental: true,
        showMileageForm: true,
        allowBlankTimecardSubmission: false
      },
      rules,
      payDayFrequencyOptions: ["Weekly", "Biweekly", "Other"],
      menu2: false,
      search: null,
      companyList: [],
      marketList: [],
      payrollCompanyList: [],
      jobTypeList: ["Commercial", "TV", "Music Video", "Live Event", "DOT"],
      companyAutocompleteIsLoading: false,
      submitButtonIsLoading: false,
      timeIncrementList: [
        {
          text: "10 Minute Increment (1/6ths)",
          value: "10 Minute Increment",
        },
        {
          text: "15 Minute Increment (1/4ths)",
          value: "15 Minute Increment",
        },
        {
          text: "6 Minute Increment (1/10ths)",
          value: "6 Minute Increment",
        },
      ],
    };
  },
  watch: {
    search: {
      deep: true,
      async handler() {
        await this.updateCompanyList();
      },
    },
  },

  async mounted() {
    if (this.mode === "EDIT") {
      const { data } = await getJob(this.$route.params.jobId);
      this.job = data;
      this.job.market = data?.market?.id;
      this.job.payrollProvider = data?.payrollProvider?.id;
      this.job.endsOn = DateTime.fromISO(this.job.endsOn).toFormat(
        "yyyy-MM-dd"
      );
      if (this.job.users.length === 0) {
        this.job.users = [{ id: "", list: [] }];
      }
      if (this.payDayFrequencyOptions.includes(this.job.payDayFrequency)) {
        this.payDayFrequency = this.job.payDayFrequency;
      }
    }
    const { data } = await getMarkets();
    this.marketList = data?.results;

    let payrollData = await getPayrollProviders();
    this.payrollCompanyList = payrollData["data"]["results"];

    this.$nextTick(() => {
      this.job = JSON.parse(JSON.stringify(this.job));
    });
  },

  methods: {
    showPayDay() {
      return this.job.market === 3 && this.job.payrollProvider == 1;
    },
    companyText(val) {
      return `${val.name} (${val.manager.email})`;
    },
    async submit() {
      // Freddy's code to validate date
      var theirEndDate = DateTime.fromFormat(
        JSON.parse(JSON.stringify(this.job)).endsOn,
        "yyyy-MM-dd"
      ).toLocal();
      var now = new Date();
      if (theirEndDate == "" || now > theirEndDate) {
        this.$emit("snackbar", {
          text: "Please select a date in the future",
          color: "red",
        });
        return;
      }
      // Checks to see if there are any validation errors
      if (!this.$refs.form.validate()) {
        this.scrollToFirstFormError();
        return;
      }
      this.submitButtonIsLoading = true;
      const job = JSON.parse(JSON.stringify(this.job));
      job.users = job.users.map((user) => user?.id).filter((user) => !!user);
      job.manager = job?.manager?.id;
      job.artCoordinator = job?.artCoordinator?.id || null;
      job.company = job?.company?.id;
      job.endsOn = DateTime.fromFormat(job.endsOn, "yyyy-MM-dd").toLocal();

      if (job.market === 3) {
        job.payDayFrequency =
          this.payDayFrequency === "Other"
            ? this.payDayFrequencyOther
            : this.payDayFrequency;
      }

      try {
        if (this.mode === "CREATE") {
          await createJob(job);
        } else {
          await updateJob(job);
        }
        this.$router.push({ name: "jobs" });
      } catch (e) {
        if (e.response.status === 502) {
          this.$emit("snackbar", { text: "502 Bad Gateway", color: "red" });
        } else {
          this.$emit("snackbar", {
            text: "Error Submitting Forms",
            color: "red",
          });
        }
      }
    },
    scrollToFirstFormError() {
      let firstInvalidInput;
      this.$refs.form.inputs.some((input) => {
        if (!input.valid) {
          firstInvalidInput = input;
        }
        return !input.valid;
      });
      if (firstInvalidInput) {
        this.$vuetify.goTo(firstInvalidInput.$el);
      }
    },
    addUser() {
      this.job.users.push({
        id: "",
        list: [],
      });
    },
    updateUser(user, i) {
      this.job.users[i] = user;
    },
    removeUser(index) {
      this.job.users.splice(index, 1);
    },
    checkType() {
      if (this.job.jobType == "Music Video") {
        this.job.payrollProvider = 3;
      }
    },
    hidePayrollProvider(){
      if (this.job.jobType == "DOT"){
        this.job.payrollProvider = 5;
      }
    }
  },
};
</script>

<style>
</style>
